import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, ShieldCheck, Award, Clock } from 'lucide-react';
export function QuoteForm() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    enquiryType: '',
    message: '',
    consent: false
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setTimeout(() => setFormState('success'), 1500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const inputClass = 'w-full border border-gray-200 py-4 px-5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-all bg-white rounded-xl';
  const labelClass = 'block text-xs font-bold uppercase tracking-widest text-primary-dark/50 mb-2 ml-1';
  return <section id="quote" className="w-full bg-[#F6F5F1] py-24 md:py-32 border-t border-gray-200" data-id="element-431">
      <div className="container mx-auto px-6" data-id="element-432">
        <div className="max-w-6xl mx-auto" data-id="element-433">
          {/* Header */}
          <div className="text-center mb-16 md:mb-20" data-id="element-434">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-accent font-bold uppercase tracking-[0.2em] text-xs mb-4 block" data-id="element-435">
              Get a Proposal
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-6xl font-bold text-primary-dark mb-6 tracking-tight" data-id="element-436">
              Request a <span className="text-accent italic" data-id="element-437">free quote</span>
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-438">
              Get expert advice and a transparent, no-obligation quote for your
              commercial HVAC needs. We aim to respond within 4 working hours.
            </motion.p>
          </div>

          {/* Form Container */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="bg-white rounded-[2.5rem] shadow-xl shadow-primary-dark/5 overflow-hidden border border-gray-100" data-id="element-439">
            {formState === 'success' ? <div className="py-24 px-8 text-center" data-id="element-440">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-441">
                  <CheckCircle className="w-10 h-10 text-green-600" data-id="element-442" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-443">
                  Quote request received
                </h3>
                <p className="text-lg text-gray-600 max-w-md mx-auto" data-id="element-444">
                  Thank you. We've received your details and our commercial team
                  will be in touch shortly to discuss your project.
                </p>
              </div> : <div className="grid lg:grid-cols-12" data-id="element-445">
                {/* Main Form Area */}
                <div className="lg:col-span-8 p-8 md:p-12 lg:p-16 border-b lg:border-b-0 lg:border-r border-gray-100" data-id="element-446">
                  <form onSubmit={handleSubmit} className="space-y-8" data-id="element-447">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-448">
                      <div data-id="element-449">
                        <label htmlFor="firstName" className={labelClass} data-id="element-450">
                          First name *
                        </label>
                        <input id="firstName" name="firstName" type="text" placeholder="John" required value={formData.firstName} onChange={handleChange} className={inputClass} data-id="element-451" />
                      </div>
                      <div data-id="element-452">
                        <label htmlFor="lastName" className={labelClass} data-id="element-453">
                          Last name *
                        </label>
                        <input id="lastName" name="lastName" type="text" placeholder="Smith" required value={formData.lastName} onChange={handleChange} className={inputClass} data-id="element-454" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-455">
                      <div data-id="element-456">
                        <label htmlFor="email" className={labelClass} data-id="element-457">
                          Work email *
                        </label>
                        <input id="email" name="email" type="email" placeholder="john@company.co.uk" required value={formData.email} onChange={handleChange} className={inputClass} data-id="element-458" />
                      </div>
                      <div data-id="element-459">
                        <label htmlFor="phone" className={labelClass} data-id="element-460">
                          Phone number *
                        </label>
                        <input id="phone" name="phone" type="tel" placeholder="01234 567 890" required value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-461" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-462">
                      <div data-id="element-463">
                        <label htmlFor="company" className={labelClass} data-id="element-464">
                          Company name *
                        </label>
                        <input id="company" name="company" type="text" placeholder="Acme Corp" required value={formData.company} onChange={handleChange} className={inputClass} data-id="element-465" />
                      </div>
                      <div data-id="element-466">
                        <label htmlFor="enquiryType" className={labelClass} data-id="element-467">
                          Type of enquiry *
                        </label>
                        <div className="relative" data-id="element-468">
                          <select id="enquiryType" name="enquiryType" required value={formData.enquiryType} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-469">
                            <option value="" data-id="element-470">Select type...</option>
                            <option value="new-installation" data-id="element-471">
                              New Installation
                            </option>
                            <option value="maintenance-service" data-id="element-472">
                              Maintenance & Service
                            </option>
                            <option value="repairs-breakdowns" data-id="element-473">
                              Repairs & Breakdowns
                            </option>
                            <option value="tm44-compliance" data-id="element-474">
                              TM44 Compliance
                            </option>
                            <option value="ventilation-systems" data-id="element-475">
                              Ventilation Systems
                            </option>
                            <option value="general-enquiry" data-id="element-476">
                              General Enquiry
                            </option>
                          </select>
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" data-id="element-477">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-478">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-479" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-480">
                      <label htmlFor="message" className={labelClass} data-id="element-481">
                        Project details
                      </label>
                      <textarea id="message" name="message" placeholder="Tell us about your building or specific requirements..." rows={4} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} data-id="element-482" />
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-4" data-id="element-483">
                      <div className="flex items-start gap-3 max-w-md" data-id="element-484">
                        <input type="checkbox" id="consent" name="consent" required checked={formData.consent} onChange={handleChange} className="mt-1.5 w-4 h-4 rounded border-gray-300 text-accent focus:ring-accent" data-id="element-485" />
                        <label htmlFor="consent" className="text-sm text-gray-500 leading-relaxed" data-id="element-486">
                          I agree to the processing of my data in accordance
                          with the{' '}
                          <a href="/privacy" className="text-primary-dark font-semibold underline underline-offset-2 hover:text-accent transition-colors" data-id="element-487">
                            Privacy Policy
                          </a>
                          .
                        </label>
                      </div>

                      <button type="submit" disabled={formState === 'loading'} className="bg-accent hover:bg-accent/90 disabled:opacity-50 text-white font-bold py-4 px-10 rounded-full transition-all shadow-lg shadow-accent/20 flex items-center justify-center gap-3 whitespace-nowrap" data-id="element-488">
                        {formState === 'loading' ? <>
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-489" />
                            <span data-id="element-490">Sending...</span>
                          </> : <span data-id="element-491">Submit Request</span>}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Sidebar Trust Area */}
                <div className="lg:col-span-4 bg-gray-50 p-8 md:p-12 flex flex-col justify-center" data-id="element-492">
                  <h4 className="text-xl font-bold text-primary-dark mb-8" data-id="element-493">
                    Why work with us?
                  </h4>
                  <div className="space-y-10" data-id="element-494">
                    <div className="flex items-start gap-4" data-id="element-495">
                      <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0" data-id="element-496">
                        <Award className="w-5 h-5 text-accent" data-id="element-497" />
                      </div>
                      <div data-id="element-498">
                        <h5 className="font-bold text-primary-dark text-sm mb-1 uppercase tracking-wider" data-id="element-499">
                          Daikin D1+ Partner
                        </h5>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-500">
                          Extended warranties and priority manufacturer support.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4" data-id="element-501">
                      <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0" data-id="element-502">
                        <ShieldCheck className="w-5 h-5 text-accent" data-id="element-503" />
                      </div>
                      <div data-id="element-504">
                        <h5 className="font-bold text-primary-dark text-sm mb-1 uppercase tracking-wider" data-id="element-505">
                          Gold Accredited
                        </h5>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-506">
                          Constructionline Gold held by just 1-2% of UK firms.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4" data-id="element-507">
                      <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0" data-id="element-508">
                        <Clock className="w-5 h-5 text-accent" data-id="element-509" />
                      </div>
                      <div data-id="element-510">
                        <h5 className="font-bold text-primary-dark text-sm mb-1 uppercase tracking-wider" data-id="element-511">
                          Rapid Response
                        </h5>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-512">
                          4-hour response guarantee for all commercial
                          enquiries.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-513">
                    <p className="text-xs text-gray-400 font-medium leading-relaxed" data-id="element-514">
                      AccuTemp Air Conditioning Ltd is a fully insured and F-Gas
                      registered commercial contractor.
                    </p>
                  </div>
                </div>
              </div>}
          </motion.div>
        </div>
      </div>
    </section>;
}